// Persian text building blocks: every phrase shows script + Finglish + English
// (the learner can hide either in Settings), can be heard, and saved to Words.
import type { ReactNode } from "react";
import { speak, useSpeech, type SpeakOptions } from "../lib/audio";
import { addWord, hasWord, useLearn } from "../lib/state";
import { track } from "../lib/analytics";
import { ISpeaker, IStop, IBookmark, IBookmarkFill } from "./icons";
import { Spinner, useToast } from "./ui";

/** Persian (RTL, Vazirmatn). Always use this for Persian so screen readers use a Persian voice. */
export function Fa({ children, className = "", size }: { children: ReactNode; className?: string; size?: "lg" | "xl" }) {
  return (
    <span lang="fa" dir="rtl" className={`fa${size ? ` fa-${size}` : ""} ${className}`}>
      {children}
    </span>
  );
}

export function SpeakButton({ text, label = "Listen", size = "md", ...o }: { text: string; label?: string; size?: "sm" | "md" } & SpeakOptions) {
  const { playing, loading } = useSpeech();
  const speed = useLearn((s) => s.profile.voiceSpeed);
  const toast = useToast();
  const t = text.trim();
  const isPlaying = playing === t;
  const isLoading = loading === t;
  return (
    <button
      type="button"
      className={`iconbtn${size === "sm" ? " sm" : ""}${isPlaying ? " active" : ""}`}
      aria-label={isPlaying ? "Stop" : label}
      title={isPlaying ? "Stop" : label}
      onClick={(e) => {
        e.stopPropagation();
        speak(t, { speed: o.speed ?? speed, ...o }).catch(() => toast("Couldn't play that. Try again.", "error"));
      }}
    >
      {isLoading ? <Spinner label="Loading audio" /> : isPlaying ? <IStop /> : <ISpeaker />}
    </button>
  );
}

export function SaveWordButton({ word, source, size = "md" }: { word: { fa: string; finglish: string; en: string; note?: string | null }; source: string; size?: "sm" | "md" }) {
  const saved = useLearn(() => hasWord(word.fa));
  const toast = useToast();
  return (
    <button
      type="button"
      className={`iconbtn${size === "sm" ? " sm" : ""}${saved ? " active" : ""}`}
      aria-label={saved ? "Saved to Words" : "Save to Words"}
      aria-pressed={saved}
      title={saved ? "In your Words" : "Save to Words"}
      onClick={(e) => {
        e.stopPropagation();
        if (saved) return;
        if (addWord(word, source)) {
          track("word_saved", { source });
          toast("Saved to Words");
        }
      }}
    >
      {saved ? <IBookmarkFill /> : <IBookmark />}
    </button>
  );
}

/** A phrase card line: Persian (big), Finglish, English, with listen + save actions. */
export function Phrase({
  fa,
  finglish,
  en,
  note,
  size = "lg",
  source,
  speakable = true,
  savable = true,
  bundledKey,
  align = "start",
}: {
  fa: string;
  finglish?: string | null;
  en?: string | null;
  note?: string | null;
  size?: "md" | "lg" | "xl";
  source?: string;
  speakable?: boolean;
  savable?: boolean;
  bundledKey?: string;
  align?: "start" | "center";
}) {
  const { showFinglish, showEnglish } = useLearn((s) => s.profile);
  return (
    <div className="stack tight" style={{ alignItems: align === "center" ? "center" : "stretch", textAlign: align === "center" ? "center" : undefined }}>
      <div className="row" style={{ justifyContent: align === "center" ? "center" : "space-between", alignItems: "flex-start", gap: 12 }}>
        <Fa size={size === "md" ? undefined : size}>{fa}</Fa>
        {(speakable || savable) && (
          <div className="row" style={{ gap: 6, flex: "none", marginTop: 4 }}>
            {speakable && <SpeakButton text={fa} hint={finglish} bundledKey={bundledKey} size="sm" />}
            {savable && finglish && en && <SaveWordButton word={{ fa, finglish, en, note }} source={source || "lesson"} size="sm" />}
          </div>
        )}
      </div>
      {showFinglish && finglish && <div className="finglish">{finglish}</div>}
      {showEnglish && en && <div className="en">{en}</div>}
      {note && <div className="small muted">{note}</div>}
    </div>
  );
}
